import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtPct, fmtQty } from "../lib/api";
import { usePullToRefresh, haptic } from "../lib/mobile";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../components/ui/sheet";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../components/ui/alert-dialog";
import { Link2, XCircle, Shield } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import IBKRLivePanel from "../components/IBKRLivePanel";
import { usePriceTicks, useSubscribePrices } from "../context/LiveStreamContext";
import { StalenessBadge } from "../components/StalenessBadge";

const COLORS = ["#00C805", "#F59E0B", "#3B82F6", "#EC4899", "#8B5CF6", "#14B8A6", "#F97316", "#EF4444"];

export default function Portfolio() {
  const [data, setData] = useState(null);
  const [ibkr, setIbkr] = useState(null);
  const [ibkrLive, setIbkrLive] = useState(null);    // {connected, account_type} from MCP
  const [liveSummary, setLiveSummary] = useState(null);  // real IBKR account summary
  const [livePositions, setLivePositions] = useState(null);
  const [mode, setMode] = useState(() => localStorage.getItem("portfolio_mode") || "paper");
  const [analytics, setAnalytics] = useState(null);
  const [stops, setStops] = useState([]);
  const [slFor, setSlFor] = useState(null);   // holding
  const [stopPx, setStopPx] = useState("");
  const [takePx, setTakePx] = useState("");
  const [trailingPct, setTrailingPct] = useState("");
  const [stopMode, setStopMode] = useState("fixed"); // "fixed" | "trailing"
  const [drawerHolding, setDrawerHolding] = useState(null);
  const [drawerChart, setDrawerChart] = useState(null);
  const [drawerOrders, setDrawerOrders] = useState([]);
  const navigate = useNavigate();

  // Load 7-day mini chart + recent orders for the drawer's asset
  useEffect(() => {
    if (!drawerHolding) { setDrawerChart(null); setDrawerOrders([]); return; }
    let alive = true;
    api.get(`/analysis/${drawerHolding.asset_type}/${drawerHolding.symbol}?range=1W`)
      .then((r) => { if (alive) setDrawerChart(r.data); })
      .catch(() => {});
    api.get("/orders").then((r) => {
      if (!alive) return;
      const rel = (r.data || []).filter((o) => o.symbol === drawerHolding.symbol && o.asset_type === drawerHolding.asset_type).slice(0, 6);
      setDrawerOrders(rel);
    }).catch(() => {});
    return () => { alive = false; };
  }, [drawerHolding]);

  const refresh = async () => {
    const [p, ib, s, mcp] = await Promise.allSettled([
      api.get("/portfolio"), api.get("/ibkr/status"), api.get("/stops"),
      api.get("/ibkr/mcp/status"),
    ]);
    if (p.status === "fulfilled") setData(p.value.data);
    if (ib.status === "fulfilled") {
      setIbkr(ib.value.data);
      if (ib.value.data?.connected) {
        try { const a = await api.get("/ibkr/analytics"); setAnalytics(a.data); } catch {}
      }
    }
    if (s.status === "fulfilled") setStops(s.value.data);
    if (mcp.status === "fulfilled") {
      setIbkrLive(mcp.value.data);
      if (mcp.value.data?.connected) {
        const [sumRes, posRes] = await Promise.allSettled([
          api.get("/ibkr/mcp/summary"), api.get("/ibkr/mcp/positions"),
        ]);
        if (sumRes.status === "fulfilled") setLiveSummary(sumRes.value.data);
        if (posRes.status === "fulfilled") setLivePositions(posRes.value.data);
      }
    }
  };

  useEffect(() => { refresh(); }, []);
  const { bind, Indicator } = usePullToRefresh(refresh);

  // Live prices: subscribe every position symbol so heatmap/table tick without polling.
  const live = usePriceTicks();
  useSubscribePrices((data?.holdings || []).map((h) => ({
    symbol: h.symbol, asset_type: h.asset_type || "stock",
  })));

  if (!data) return <div className="text-muted-foreground">Loading...</div>;

  // If Live mode selected but IBKR not connected, fall back to paper silently
  const effectiveMode = mode === "live" && ibkrLive?.connected ? "live" : "paper";

  // Compute viewData based on selected mode — Paper uses Aanar's ledger; Live pulls from MCP.
  const viewData = (() => {
    if (effectiveMode !== "live" || !liveSummary?.summary) {
      // Paper mode: overlay live WS ticks onto persisted holdings so the
      // heatmap re-tints and total P&L recomputes every 10s without a refetch.
      if (!data) return data;
      let deltaPnl = 0;
      const holdings = (data.holdings || []).map((h) => {
        const tick = live[h.symbol];
        if (!tick || tick.p == null) return h;
        const oldVal = h.value || 0;
        const newVal = (h.quantity || 0) * tick.p;
        const oldPnl = h.pnl || 0;
        const newPnl = oldPnl + (newVal - oldVal);
        deltaPnl += (newVal - oldVal);
        return {
          ...h,
          current_price: tick.p,
          value: newVal,
          change_pct: tick.ch,
          pnl: newPnl,
          _live: true,
        };
      });
      const total_pnl = (data.total_pnl || 0) + deltaPnl;
      const total_equity = (data.cash_balance || 0) + holdings.reduce((s, h) => s + (h.value || 0), 0);
      return { ...data, holdings, total_pnl,
               total_pnl_pct: total_equity ? (total_pnl / (total_equity - total_pnl)) * 100 : 0,
               total_equity,
               invested_value: holdings.reduce((s, h) => s + (h.value || 0), 0) };
    }
    const s = liveSummary.summary || {};
    const posRaw = livePositions?.positions?.positions || livePositions?.positions || [];
    const posArr = Array.isArray(posRaw) ? posRaw : [];
    const netLiq = Number(s.net_liquidation || s.equity_with_loan_value || 0);
    const cash = Number(s.total_cash_value || s.cash || 0);
    const invested = Math.max(0, netLiq - cash);
    // Map IBKR positions to Aanar's holding shape so the heatmap/table can reuse.
    const holdings = posArr.map((p) => ({
      symbol: p.symbol || p.ticker || p.contract,
      ticker: p.symbol || p.ticker || p.contract,
      name: p.name || p.symbol,
      asset_type: (p.asset_class || p.assetClass || "stock").toLowerCase() === "crypto" ? "crypto" : "stock",
      quantity: Number(p.quantity ?? p.position ?? p.size ?? 0),
      current_price: Number(p.market_price ?? p.price ?? 0),
      value: Number(p.market_value ?? (p.quantity * (p.market_price || 0)) ?? 0),
      pnl: Number(p.unrealized_pnl ?? p.dayPnl ?? 0),
      pnl_pct: Number(p.day_change_pct ?? p.unrealized_pnl_pct ?? 0),
      avg_cost: Number(p.average_cost ?? p.avg_cost ?? 0),
    }));
    const dayPnl = holdings.reduce((s, h) => s + (h.pnl || 0), 0);
    return {
      total_equity: netLiq,
      invested_value: invested,
      cash_balance: cash,
      total_pnl: dayPnl,
      total_pnl_pct: netLiq ? (dayPnl / netLiq) * 100 : 0,
      holdings,
      allocation: (data.allocation || []),
    };
  })();

  const activeStops = stops.filter((s) => s.status === "active");
  const stopFor = (h) => activeStops.find((s) => s.symbol === h.symbol && s.asset_type === h.asset_type);

  const alloc = viewData.holdings.map((h) => ({ name: h.ticker, value: h.value }));
  const total = data.total_equity || 1;

  return (
    <div className="space-y-6 h-full overflow-y-auto -mx-4 md:mx-0 px-4 md:px-0" data-testid="portfolio-page" {...bind}>
      <Indicator />
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
            Your positions
            <StalenessBadge symbols={(viewData.holdings || []).map((h) => ({ symbol: h.symbol }))}
                            testId="portfolio-freshness" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">Portfolio</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {viewData.holdings.length > 0 && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="outline" className="rounded-full border-[hsl(var(--loss))]/40 text-[hsl(var(--loss))] hover:bg-[hsl(var(--loss))]/10"
                        data-testid="close-all-btn">
                  <XCircle className="w-4 h-4 mr-2" /> Close all positions
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="close-all-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>Close all {viewData.holdings.length} position{viewData.holdings.length === 1 ? "" : "s"}?</AlertDialogTitle>
                  <AlertDialogDescription>
                    A market sell order will be placed for every holding. Estimated proceeds:{" "}
                    <span className="font-mono-tab font-medium text-foreground">{fmtMoney(data.invested_value)}</span>.
                    This can't be undone.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel data-testid="close-all-cancel">Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    data-testid="close-all-confirm"
                    className="bg-[hsl(var(--loss))] hover:bg-[hsl(var(--loss))]/90 text-black"
                    onClick={async () => {
                      try {
                        const { data: r } = await api.post("/portfolio/close-all");
                        haptic("sell");
                        toast.success(`Closed ${r.closed} positions · ${fmtMoney(r.proceeds)} back to cash`);
                        if (r.errors?.length) toast.error(`${r.errors.length} failed`);
                        refresh();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Close-all failed");
                      }
                    }}>
                    Close everything
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      </div>

      <IBKRLivePanel />

      {/* Paper / Live context toggle — swaps all equity/cash/P&L tiles below */}
      <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="portfolio-mode-strip">
        <div className="inline-flex bg-secondary/60 rounded-full p-0.5" data-testid="portfolio-mode-toggle">
          <button onClick={() => { setMode("paper"); localStorage.setItem("portfolio_mode", "paper"); }}
                  data-testid="portfolio-mode-paper"
                  className={`px-3 py-1 text-xs font-medium rounded-full uppercase tracking-wider transition ${mode === "paper" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
            Paper (Sim)
          </button>
          <button onClick={() => { setMode("live"); localStorage.setItem("portfolio_mode", "live"); }}
                  disabled={!ibkrLive?.connected}
                  aria-disabled={!ibkrLive?.connected}
                  data-testid="portfolio-mode-live"
                  className={`px-3 py-1 text-xs font-medium rounded-full uppercase tracking-wider transition ${mode === "live" ? "bg-emerald-500/15 text-emerald-400 shadow-sm" : "text-muted-foreground"} disabled:opacity-40 disabled:cursor-not-allowed`}>
            Live (IBKR)
            {!ibkrLive && <span className="ml-1 text-[9px] normal-case tracking-normal">connect first</span>}
          </button>
        </div>
        <div className="text-[11px] text-muted-foreground">
          {mode === "paper"
            ? "Aanar virtual $100k · sim orders · live IBKR prices"
            : "Your real IBKR account · MCP-native · confirms via AI Instructions"}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4" data-testid="portfolio-tiles">
        <Card className="p-6 rounded-xl border-border">
          <div className="text-xs uppercase text-muted-foreground">Total equity</div>
          <div className="font-mono-tab text-2xl font-semibold mt-1">{fmtMoney(viewData.total_equity)}</div>
        </Card>
        <Card className="p-6 rounded-xl border-border">
          <div className="text-xs uppercase text-muted-foreground">Invested</div>
          <div className="font-mono-tab text-2xl font-semibold mt-1">{fmtMoney(viewData.invested_value)}</div>
        </Card>
        <Card className="p-6 rounded-xl border-border">
          <div className="text-xs uppercase text-muted-foreground">Cash</div>
          <div className="font-mono-tab text-2xl font-semibold mt-1">{fmtMoney(viewData.cash_balance)}</div>
        </Card>
        <Card className="p-6 rounded-xl border-border">
          <div className="text-xs uppercase text-muted-foreground">Total P&L</div>
          <div className={`font-mono-tab text-2xl font-semibold mt-1 ${viewData.total_pnl >= 0 ? "gain" : "loss"}`}>{fmtMoney(viewData.total_pnl)}</div>
          <div className={`font-mono-tab text-xs ${viewData.total_pnl >= 0 ? "gain" : "loss"}`}>{fmtPct(viewData.total_pnl_pct)}</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-3 p-4 md:p-5 rounded-xl border-border" data-testid="heatmap-card">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Heatmap</div>
              <div className="font-display font-semibold">Positions today</div>
            </div>
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-wider text-muted-foreground">
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[hsl(var(--loss))]" /> −3%+</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[hsl(var(--loss))]/40" /> −1%</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-secondary" /> flat</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[hsl(var(--gain))]/40" /> +1%</span>
              <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[hsl(var(--gain))]" /> +3%+</span>
            </div>
          </div>
          {viewData.holdings.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-6">No positions yet — buy something to see the heatmap.</div>
          ) : (
            <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))" }} data-testid="heatmap-grid">
              {(() => {
                const maxVal = Math.max(...viewData.holdings.map((h) => h.value || 0), 1);
                const colorFor = (pct) => {
                  const p = Math.max(-5, Math.min(5, pct || 0));  // clamp -5..+5
                  const alpha = Math.min(0.95, 0.15 + Math.abs(p) / 5 * 0.85);
                  return p >= 0
                    ? `hsl(var(--gain) / ${alpha})`
                    : `hsl(var(--loss) / ${alpha})`;
                };
                // Sort by value descending so biggest tiles appear first
                const sorted = [...viewData.holdings].sort((a, b) => (b.value || 0) - (a.value || 0));
                return sorted.map((h) => {
                  const weight = Math.max(0.35, (h.value || 0) / maxVal);   // relative size 0.35-1
                  // Convert weight → grid column span 1..4
                  const span = Math.round(1 + (weight - 0.35) / 0.65 * 3);
                  const clampSpan = Math.max(1, Math.min(4, span));
                  const isFlat = Math.abs(h.change_pct || 0) < 0.2;
                  return (
                    <button
                      key={`${h.asset_type}:${h.symbol}`}
                      onClick={() => setDrawerHolding(h)}
                      data-testid={`heatmap-tile-${h.symbol}`}
                      className="relative p-3 rounded-lg text-left transition-transform hover:scale-[1.02] border border-border/40"
                      style={{
                        gridColumn: `span ${clampSpan}`,
                        minHeight: 80,
                        backgroundColor: isFlat ? "hsl(var(--secondary) / 0.6)" : colorFor(h.change_pct),
                      }}
                      title={`${h.ticker || h.symbol} · ${h.name || ""}\n${fmtMoney(h.value)} · ${(h.change_pct || 0).toFixed(2)}% today`}>
                      <div className="text-sm font-medium font-mono-tab truncate" style={{ color: "black" }}>{h.ticker || h.symbol}</div>
                      <div className="text-[10px] mt-0.5 font-mono-tab" style={{ color: "rgba(0,0,0,0.75)" }}>
                        {(h.change_pct || 0) >= 0 ? "+" : ""}{(h.change_pct || 0).toFixed(2)}%
                      </div>
                      <div className="text-[10px] mt-0.5 font-mono-tab" style={{ color: "rgba(0,0,0,0.65)" }}>
                        {fmtMoney(h.value, 0)}
                      </div>
                      {/* Position sparkline — last 7 daily closes */}
                      {h.sparkline?.length > 1 && (() => {
                        const arr = h.sparkline;
                        const lo = Math.min(...arr), hi = Math.max(...arr);
                        const range = hi - lo || 1;
                        const up = arr[arr.length - 1] >= arr[0];
                        const stroke = up ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0.7)";
                        const pts = arr.map((v, i) => {
                          const x = (i / (arr.length - 1)) * 100;
                          const y = 30 - ((v - lo) / range) * 26;
                          return `${x.toFixed(1)},${y.toFixed(1)}`;
                        }).join(" ");
                        return (
                          <svg viewBox="0 0 100 32" className="w-full mt-1 pointer-events-none" preserveAspectRatio="none"
                               data-testid={`sparkline-${h.symbol}`}>
                            <polyline points={pts} fill="none" stroke={stroke} strokeWidth="1.5"
                                      strokeLinejoin="round" strokeLinecap="round" opacity="0.85" />
                          </svg>
                        );
                      })()}
                    </button>
                  );
                });
              })()}
            </div>
          )}
        </Card>

        <Card className="lg:col-span-2 p-6 rounded-xl border-border">
          <div className="font-display font-semibold mb-4">Holdings</div>
          {viewData.holdings.length ? (
            <div className="rounded-lg overflow-hidden border border-border">
              <div className="hidden sm:grid grid-cols-12 text-xs uppercase tracking-wider text-muted-foreground px-4 py-3 bg-secondary/40">
                <div className="col-span-3">Asset</div>
                <div className="col-span-2 text-right">Qty</div>
                <div className="col-span-2 text-right">Avg cost</div>
                <div className="col-span-2 text-right">Price</div>
                <div className="col-span-2 text-right">Value</div>
                <div className="col-span-1 text-right">P&L</div>
              </div>
              <div className="divide-y divide-border">
                {viewData.holdings.map((h) => {
                  const s = stopFor(h);
                  return (
                  <div key={`${h.asset_type}:${h.symbol}`}
                       data-testid={`portfolio-row-${h.symbol}`}
                       className="grid grid-cols-2 sm:grid-cols-12 gap-y-1 items-center px-4 py-3 hover:bg-secondary/40">
                    <div className="sm:col-span-3 cursor-pointer" onClick={() => navigate(`/asset/${h.asset_type}/${h.symbol}`)}>
                      <div className="font-medium text-sm flex items-center gap-1.5">
                        {h.ticker}
                        {s && <Shield className={`w-3 h-3 ${s.kind === "trailing_stop" ? "text-[hsl(var(--ai))]" : "text-[hsl(var(--ai))]"}`}
                                     title={s.kind === "trailing_stop"
                                       ? `Trailing ${s.trail_pct}% · current stop $${s.stop_price ?? '—'} · peak $${s.peak_price ?? '—'}`
                                       : `Stop $${s.stop_price ?? '—'} / TP $${s.take_price ?? '—'}`} />}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{h.name}</div>
                    </div>
                    <div className="hidden sm:block sm:col-span-2 text-right font-mono-tab text-sm">{fmtQty(h.quantity)}</div>
                    <div className="hidden sm:block sm:col-span-1 text-right font-mono-tab text-sm">{fmtMoney(h.cost_basis / h.quantity)}</div>
                    <div className="hidden sm:block sm:col-span-2 text-right font-mono-tab text-sm">{fmtMoney(h.current_price)}</div>
                    <div className="text-right sm:col-span-2 font-mono-tab text-sm">{fmtMoney(h.value)}</div>
                    <div
                      data-testid={`portfolio-pnl-${h.symbol}`}
                      className={`col-start-2 row-start-1 sm:col-start-auto sm:row-start-auto sm:col-span-1 text-right font-mono-tab text-sm whitespace-nowrap ${h.pnl >= 0 ? "gain" : "loss"}`}
                    >
                      {h.pnl >= 0 ? "+" : "-"}{fmtMoney(Math.abs(h.pnl))}
                      <div className="text-[10px] opacity-70">{fmtPct(h.pnl_pct)}</div>
                    </div>
                    <div className="col-span-2 sm:col-span-1 flex justify-end">
                      {s ? (
                        <button
                          data-testid={`stop-cancel-${h.symbol}`}
                          className="text-[10px] uppercase text-[hsl(var(--ai))] hover:underline"
                          onClick={async () => { await api.delete(`/stops/${s.id}`); toast.success("Stop cancelled"); refresh(); }}
                        >Clear SL/TP</button>
                      ) : (
                        <button
                          data-testid={`stop-add-${h.symbol}`}
                          className="text-[10px] uppercase text-muted-foreground hover:text-foreground"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSlFor(h);
                            setStopPx(String((h.current_price * 0.9).toFixed(2)));
                            setTakePx(String((h.current_price * 1.2).toFixed(2)));
                          }}
                        >Add SL/TP</button>
                      )}
                    </div>
                  </div>);
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-16 text-sm text-muted-foreground">No holdings yet.</div>
          )}
        </Card>

        <Card className="p-6 rounded-xl border-border" data-testid="allocation-chart">
          <div className="font-display font-semibold mb-4">Allocation</div>
          {alloc.length ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={alloc} innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={2}>
                  {alloc.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v) => fmtMoney(v)} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }}/>
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-sm text-muted-foreground py-10 text-center">No positions to allocate.</div>
          )}
          <div className="mt-4 space-y-1.5">
            {alloc.map((a, i) => (
              <div key={`${a.asset_type || "x"}:${a.symbol || a.name}:${i}`} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                  <span>{a.name}</span>
                </div>
                <span className="font-mono-tab text-muted-foreground">{((a.value / total) * 100).toFixed(1)}%</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {analytics && (
        <Card className="p-6 rounded-xl border-border" data-testid="broker-analytics">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <Link2 className="w-4 h-4" />
              <div className="font-display font-semibold">Broker analytics</div>
              <span className="text-xs text-muted-foreground uppercase">IBKR · {analytics.mode} · {analytics.account_id}</span>
              {analytics.is_mock ? (
                <span data-testid="analytics-simulated-badge" className="text-[10px] uppercase tracking-widest font-medium text-amber-500 bg-amber-500/10 border border-amber-500/30 rounded-full px-2 py-0.5">
                  Simulated · not live IBKR
                </span>
              ) : (
                <span data-testid="analytics-live-badge" className="text-[10px] uppercase tracking-widest font-medium text-[hsl(var(--gain))] bg-[hsl(var(--gain))]/10 border border-[hsl(var(--gain))]/30 rounded-full px-2 py-0.5">
                  Live from CPGW
                </span>
              )}
            </div>
          </div>
          {analytics.is_mock && (
            <div data-testid="analytics-mock-banner" className="mb-4 text-xs bg-amber-500/10 border border-amber-500/30 text-amber-500 rounded-md p-3 flex gap-2">
              <XCircle className="w-4 h-4 shrink-0 mt-0.5" />
              <div>
                <b>These numbers are simulated by Aanar, not pulled from IBKR.</b>
                <div className="text-muted-foreground mt-1">
                  {analytics.note || "Connect a working Client Portal Gateway on the /ibkr page to see real broker analytics."}
                  {" "}
                  <a href="/ibkr" className="underline text-[hsl(var(--ai))]">Open the setup wizard →</a>
                </div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div>
              <div className="text-xs uppercase text-muted-foreground">Fills</div>
              <div className="font-mono-tab text-xl font-semibold mt-1" data-testid="analytics-fills">{analytics.n_fills}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-muted-foreground">Volume traded</div>
              <div className="font-mono-tab text-xl font-semibold mt-1">{fmtMoney(analytics.total_volume)}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-muted-foreground">Commissions</div>
              <div className="font-mono-tab text-xl font-semibold mt-1 loss">{fmtMoney(analytics.total_commission)}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-muted-foreground">Margin used</div>
              <div className="font-mono-tab text-xl font-semibold mt-1">{fmtMoney(analytics.margin_used)}</div>
            </div>
            <div>
              <div className="text-xs uppercase text-muted-foreground">Buying power</div>
              <div className="font-mono-tab text-xl font-semibold mt-1 gain">{fmtMoney(analytics.buying_power)}</div>
            </div>
          </div>
          {analytics.recent_fills?.length > 0 && (
            <div className="mt-6 pt-4 border-t border-border">
              <div className="text-xs uppercase text-muted-foreground mb-2">Recent broker fills</div>
              <div className="space-y-1">
                {analytics.recent_fills.map((f) => (
                  <div key={f.id} className="grid grid-cols-6 text-xs py-1 items-center" data-testid={`fill-${f.id}`}>
                    <span className="font-medium">{f.ticker}</span>
                    <span className={`uppercase font-medium ${f.side === "buy" ? "gain" : "loss"}`}>{f.side}</span>
                    <span className="font-mono-tab">{f.quantity}</span>
                    <span className="font-mono-tab">{fmtMoney(f.price)}</span>
                    <span className="font-mono-tab text-muted-foreground">comm {fmtMoney(f.commission || 0)}</span>
                    <span className="font-mono-tab text-muted-foreground text-right"
                          title={new Date(f.created_at).toLocaleString()}>
                      {(() => {
                        const d = new Date(f.created_at);
                        const now = new Date();
                        const isToday = d.toDateString() === now.toDateString();
                        return isToday
                          ? d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })
                          : d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) + " " + d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
                      })()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </Card>
      )}

      {/* SL/TP Dialog */}
      <Dialog open={!!slFor} onOpenChange={(o) => !o && setSlFor(null)}>
        <DialogContent data-testid="sltp-dialog">
          <DialogHeader>
            <DialogTitle>Stop-loss & take-profit — {slFor?.ticker}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="text-sm text-muted-foreground">
              Current price: <span className="font-mono-tab text-foreground">{fmtMoney(slFor?.current_price)}</span>{" "}
              · Qty: <span className="font-mono-tab text-foreground">{fmtQty(slFor?.quantity)}</span>
            </div>

            {/* Fixed vs Trailing mode toggle */}
            <div className="inline-flex bg-secondary/50 rounded-full p-0.5" data-testid="sltp-mode-toggle">
              <button onClick={() => setStopMode("fixed")}
                      data-testid="sltp-mode-fixed"
                      className={`px-3 py-1 text-[11px] font-medium rounded-full uppercase tracking-wider transition ${stopMode === "fixed" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>
                Fixed
              </button>
              <button onClick={() => setStopMode("trailing")}
                      data-testid="sltp-mode-trailing"
                      className={`px-3 py-1 text-[11px] font-medium rounded-full uppercase tracking-wider transition ${stopMode === "trailing" ? "bg-[hsl(var(--ai))]/15 text-[hsl(var(--ai))] shadow-sm" : "text-muted-foreground"}`}>
                Trailing
              </button>
            </div>

            {stopMode === "fixed" ? (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Stop-loss price</Label>
                  <Input type="number" min="0" step="any" value={stopPx} onChange={(e) => setStopPx(e.target.value)}
                         data-testid="stop-price" className="mt-1 font-mono-tab" />
                  <div className="text-[11px] loss mt-1">Auto-sells if price falls to this level</div>
                </div>
                <div>
                  <Label>Take-profit price</Label>
                  <Input type="number" min="0" step="any" value={takePx} onChange={(e) => setTakePx(e.target.value)}
                         data-testid="take-price" className="mt-1 font-mono-tab" />
                  <div className="text-[11px] gain mt-1">Auto-sells if price rises to this level</div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Trail %</Label>
                  <Input type="number" min="0.1" max="50" step="0.5" value={trailingPct}
                         onChange={(e) => setTrailingPct(e.target.value)}
                         data-testid="trailing-pct" className="mt-1 font-mono-tab" placeholder="5" />
                  <div className="text-[11px] text-muted-foreground mt-1">
                    Stop follows price up · fires this % below the peak
                  </div>
                  {trailingPct && slFor?.current_price && (
                    <div className="text-[11px] mt-1 font-mono-tab">
                      Initial stop: <span className="loss">{fmtMoney(slFor.current_price * (1 - parseFloat(trailingPct) / 100))}</span>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Take-profit price (optional)</Label>
                  <Input type="number" min="0" step="any" value={takePx} onChange={(e) => setTakePx(e.target.value)}
                         data-testid="take-price-trail" className="mt-1 font-mono-tab" />
                  <div className="text-[11px] gain mt-1">Optional hard cap on top of the trail</div>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSlFor(null)} className="rounded-full">Cancel</Button>
            <Button data-testid="sltp-save"
              onClick={async () => {
                try {
                  const body = {
                    symbol: slFor.symbol, asset_type: slFor.asset_type,
                    quantity: slFor.quantity,
                    take_price: takePx ? parseFloat(takePx) : null,
                    direction: "long", route: "sim",
                  };
                  if (stopMode === "trailing") {
                    const t = parseFloat(trailingPct);
                    if (!t || t <= 0) return toast.error("Enter a trail %");
                    body.trailing_pct = t;
                  } else {
                    body.stop_price = stopPx ? parseFloat(stopPx) : null;
                  }
                  await api.post("/stops", body);
                  toast.success(`${stopMode === "trailing" ? "Trailing stop" : "SL/TP"} set on ${slFor.ticker}`);
                  setSlFor(null); setStopPx(""); setTakePx(""); setTrailingPct(""); setStopMode("fixed");
                  refresh();
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Failed");
                }
              }}
              className="rounded-full bg-foreground text-background hover:bg-foreground/90"
            >
              <Shield className="w-4 h-4 mr-2" /> {stopMode === "trailing" ? "Set trailing stop" : "Set SL/TP"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Heatmap detail drawer — quick view + close */}
      <Sheet open={!!drawerHolding} onOpenChange={(o) => !o && setDrawerHolding(null)}>
        <SheetContent side="bottom" className="h-[70vh] rounded-t-2xl" data-testid="heatmap-drawer">
          {drawerHolding && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono-tab text-2xl">{drawerHolding.ticker || drawerHolding.symbol}</span>
                  <span className={`text-lg font-mono-tab ${(drawerHolding.change_pct || 0) >= 0 ? "gain" : "loss"}`}>
                    {(drawerHolding.change_pct || 0) >= 0 ? "+" : ""}{(drawerHolding.change_pct || 0).toFixed(2)}%
                  </span>
                  <span className="text-sm text-muted-foreground font-normal">{drawerHolding.name}</span>
                </SheetTitle>
                <SheetDescription className="text-xs">
                  Position size {fmtMoney(drawerHolding.value)} · avg cost {fmtMoney(drawerHolding.cost_basis / (drawerHolding.quantity || 1))} · unrealized <span className={drawerHolding.pnl >= 0 ? "gain" : "loss"}>{drawerHolding.pnl >= 0 ? "+" : ""}{fmtMoney(drawerHolding.pnl)}</span>
                </SheetDescription>
              </SheetHeader>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Mini 7-day chart */}
                <div className="md:col-span-2">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Last 7 days</div>
                  {drawerChart?.candles?.length ? (
                    <svg viewBox="0 0 600 160" className="w-full h-40" data-testid="drawer-mini-chart">
                      {(() => {
                        const c = drawerChart.candles;
                        const lo = Math.min(...c.map((x) => x.l));
                        const hi = Math.max(...c.map((x) => x.h));
                        const pad = (hi - lo) * 0.08 || 1;
                        const yTop = hi + pad, yBot = lo - pad;
                        const scaleY = (v) => 10 + ((yTop - v) / (yTop - yBot)) * 140;
                        const stepX = 580 / c.length;
                        return (
                          <>
                            {c.map((cd, i) => {
                              const x = 10 + i * stepX + stepX / 2;
                              const isBull = cd.c >= cd.o;
                              const clr = isBull ? "hsl(var(--gain))" : "hsl(var(--loss))";
                              const bt = scaleY(Math.max(cd.o, cd.c));
                              const bb = scaleY(Math.min(cd.o, cd.c));
                              return (
                                <g key={i}>
                                  <line x1={x} x2={x} y1={scaleY(cd.h)} y2={scaleY(cd.l)} stroke={clr} strokeWidth="0.8" />
                                  <rect x={x - stepX * 0.35} y={bt} width={stepX * 0.7} height={Math.max(1, bb - bt)} fill={clr} />
                                </g>
                              );
                            })}
                            {drawerHolding.quantity > 0 && (() => {
                              const avg = drawerHolding.cost_basis / drawerHolding.quantity;
                              if (avg >= yBot && avg <= yTop) {
                                return (
                                  <>
                                    <line x1={10} x2={590} y1={scaleY(avg)} y2={scaleY(avg)}
                                          stroke="hsl(var(--ai))" strokeDasharray="4 3" opacity="0.8" />
                                    <text x={12} y={scaleY(avg) - 3} fill="hsl(var(--ai))" fontSize="9" fontFamily="JetBrains Mono">avg ${avg.toFixed(2)}</text>
                                  </>
                                );
                              }
                              return null;
                            })()}
                          </>
                        );
                      })()}
                    </svg>
                  ) : (
                    <div className="text-xs text-muted-foreground h-40 flex items-center justify-center">Loading…</div>
                  )}
                </div>

                {/* Actions */}
                <div className="space-y-2">
                  <Button
                    onClick={async () => {
                      try {
                        // Auto-close entire position at market
                        await api.post("/orders", {
                          symbol: drawerHolding.symbol, asset_type: drawerHolding.asset_type,
                          side: "sell", quantity: drawerHolding.quantity, route: "sim",
                        });
                        toast.success(`Closed ${drawerHolding.ticker} · ${fmtQty(drawerHolding.quantity)} sold`);
                        setDrawerHolding(null); refresh();
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || "Close failed");
                      }
                    }}
                    data-testid="drawer-close-position"
                    className="w-full rounded-full bg-[hsl(var(--loss))] hover:bg-[hsl(var(--loss))]/90 text-black">
                    Close position
                  </Button>
                  <Button
                    onClick={() => { setDrawerHolding(null); navigate(`/asset/${drawerHolding.asset_type}/${drawerHolding.symbol}`); }}
                    data-testid="drawer-open-asset"
                    variant="outline" className="w-full rounded-full">
                    Open full view
                  </Button>
                  <Button
                    onClick={() => { setSlFor(drawerHolding); setDrawerHolding(null); }}
                    data-testid="drawer-set-stop"
                    variant="outline" className="w-full rounded-full">
                    Set SL / TP
                  </Button>
                </div>
              </div>

              {/* Recent trades + notes */}
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Recent orders & notes</div>
                <div className="divide-y divide-border rounded-lg border border-border">
                  {drawerOrders.length ? drawerOrders.map((o) => (
                    <div key={o.id} className="grid grid-cols-12 items-center px-3 py-2 text-xs" data-testid={`drawer-order-${o.id}`}>
                      <div className="col-span-2 text-muted-foreground font-mono-tab">{new Date(o.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
                      <div className="col-span-2">
                        <span className={`uppercase text-[10px] font-medium px-2 py-0.5 rounded-full ${o.side === "buy" ? "bg-[hsl(var(--gain))]/15 gain" : "bg-[hsl(var(--loss))]/15 loss"}`}>{o.side}</span>
                      </div>
                      <div className="col-span-3 font-mono-tab">{fmtQty(o.quantity)} @ {fmtMoney(o.price)}</div>
                      <div className="col-span-5 text-muted-foreground italic truncate">{o.notes || "—"}</div>
                    </div>
                  )) : <div className="p-4 text-xs text-muted-foreground text-center">No orders yet.</div>}
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
